//import { inject } from '@angular/core'
import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http'
import { inject } from '@angular/core'
import { Store } from '@ngrx/store'
import { catchError, exhaustMap, first, map, throwError } from 'rxjs'
import { selectAccessToken } from '../store/auth/auth.selectors'
import { Router } from '@angular/router'
import { eps } from '../services/url.service'
import { loadCurrentUser, logout } from '../store/auth/auth.actions'

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store)
  const router = inject(Router)
  //Todo : make excluded urls dynamic
  const excludedUrls = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    'country',
    'state',
    'city',
    '/assets/',
    '/uploads/'
  ]
  // console.log('request url interceptor', req.url)

  // Check if request should be excluded
  if (excludedUrls.some(url => req.url.includes(url))) {
    return next(req) // Skip adding accessToken
    // return next(req.clone({ withCredentials: true }))
  }

  // ✅ Skip Authorization for static image URLs (avoids preflight!)
  if (req.url.includes('/uploads/')) {
    // return next(req.clone({ setHeaders: {}, withCredentials: true }))
    return next(req.clone({ setHeaders: {} }))
  }

  return store.select(selectAccessToken).pipe(
    first(), // Take only the first value (no need to listen indefinitely)
    exhaustMap(accessToken => {
      let authReq = req
      if (accessToken) {
        authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${accessToken}`
            // 'X-Refresh-Token': localStorage.getItem('refreshToken') || '', // Custom header
            // 'Content-Type': 'application/json',
            // Origin: 'http://localhost:4200', // Add the Origin header
            // 'Access-Control-Allow-Origin': '*'
          }
          // withCredentials: true
        })
      } else {
        authReq = req.clone({
          // withCredentials: true // 👈 even if no token, still send cookies
        })
      }
      return next(authReq).pipe(
        map(event => {
          if (event instanceof HttpResponse) {
            const body = event.body
            let newEvent = event // By default, return the original event

            // Check if body is an object (non-array) with only one property "forceRefresh"
            if (
              body &&
              typeof body === 'object' &&
              !Array.isArray(body) &&
              Object.keys(body).length === 1 &&
              Object.prototype.hasOwnProperty.call(body, 'forceRefresh') // Or 'forceRefresh' in body for inherited properties
            ) {
              const forceRefreshValue = (body as { forceRefresh: boolean }).forceRefresh
              // Make the body effectively empty
              newEvent = event.clone({ body: null })

              if (forceRefreshValue === true) store.dispatch(loadCurrentUser())
              return newEvent // Return the cloned event with the empty body
            }

            if (
              Array.isArray(body) &&
              body.length > 0 &&
              Array.isArray(body[body.length - 1]) &&
              body[body.length - 1].length > 0 &&
              Array.isArray(body[body.length - 1][0]) && // Ensure the inner element is an array
              body[body.length - 1][0].length > 1 && // Ensure it has at least two items
              body[body.length - 1][0][1] === true // Check the forceRefresh boolean
            ) {
              //forceRefresh (array, last item) is true, dispatching loadCurrentUser and removing item.
              const newBody = [...body] // Clone array before pop for immutability if passing to clone
              newBody.pop()
              store.dispatch(loadCurrentUser())
              newEvent = event.clone({ body: newBody })
              return newEvent
            }

            // Check if body is an array and the last item has `forceRefresh: false`
            if (
              Array.isArray(body) &&
              body.length > 0 &&
              Array.isArray(body[body.length - 1]) &&
              body[body.length - 1].length > 0 &&
              Array.isArray(body[body.length - 1][0]) &&
              body[body.length - 1][0].length > 1 &&
              body[body.length - 1][0][1] === false
            ) {
              //forceRefresh (array, last item) is false, removing item.
              const newBody = [...body]
              newBody.pop()
              newEvent = event.clone({ body: newBody })
              return newEvent
            }

            // Also check for non-array case (object response) with forceRefresh but NOT as the only key
            if (
              body &&
              typeof body === 'object' &&
              !Array.isArray(body) &&
              'forceRefresh' in body &&
              Object.keys(body).length > 1
            ) {
              const { forceRefresh, ...rest } = body
              if (forceRefresh === true) {
                store.dispatch(loadCurrentUser())
              }
              newEvent = event.clone({ body: rest })
              return newEvent
            }
          }
          return event // Return original or modified event
        }),
        catchError(error => {
          if (
            error instanceof HttpErrorResponse &&
            error.status === 401 &&
            router.url !== eps.auth.loginPath
          ) {
            localStorage.clear()
            store.dispatch(logout())
            router.navigate([eps.auth.loginPath])
          }
          return throwError(() => error)
        })
      )
    })
  )
}
